import { useCallback, useEffect, useRef } from "react";

import {
  ONBOARDING_STEPS,
  type OnboardingStepId,
} from "#modules/onboardingV2/domain/onboardingSteps";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";

export type OnboardingAnalyticsStep = OnboardingStepId | "welcome" | "finish";

type OnboardingAnalyticsEventName = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

const getStepPosition = (step: OnboardingAnalyticsStep) =>
  step === "welcome" || step === "finish" ? null : ONBOARDING_STEPS[step].position;

export const useOnboardingAnalytics = (step: OnboardingAnalyticsStep) => {
  const analytics = useAnalytics();

  const captureOnboardingEvent = useCallback(
    (event: OnboardingAnalyticsEventName, properties?: Record<string, unknown>) => {
      analytics.capture(event, {
        onboardingVersion: "v2",
        step,
        stepPosition: getStepPosition(step),
        ...properties,
      });
    },
    [analytics, step],
  );

  return { captureOnboardingEvent };
};

export const useTrackOnboardingStepViewed = (step: OnboardingAnalyticsStep) => {
  const { captureOnboardingEvent } = useOnboardingAnalytics(step);
  const hasTrackedStepView = useRef(false);

  useEffect(() => {
    if (hasTrackedStepView.current) {
      return;
    }

    hasTrackedStepView.current = true;
    captureOnboardingEvent(AnalyticsEvent.OnboardingStepViewed);
  }, [captureOnboardingEvent]);
};
